import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getInstructions } from '../../api/pmsApi';

function InstructionList({ type = 'received' }) {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // 페이징 관련 상태
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  // 검색 관련 상태
  const [searchType, setSearchType] = useState('project_name');
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [hoveredPmInfo, setHoveredPmInfo] = useState(null);
  const [guidePos, setGuidePos] = useState(null);

  // 진행 상태 뱃지 매핑
  const statusBadgeMap = {
    // Instruction & 공통 상태
    pending: { label: '대기중', className: 'bg-warning text-dark' },
    under_review: { label: '검토중', className: 'bg-info text-dark' },
    approved: { label: '승인됨', className: 'bg-success' },
    rejected: { label: '반려됨', className: 'bg-danger' },
    completed: { label: '완료됨', className: 'bg-dark text-white' },
    canceled: { label: '취소됨', className: 'bg-secondary' },
    // Project 전용 상태
    ready: { label: '진행중', className: 'bg-primary text-white' },
    review: { label: '검토중', className: 'bg-info text-dark' },
    in_progress: { label: '진행중', className: 'bg-primary text-white' }
  };

  const fetchInstructions = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = isSearching
        ? { type, ...searchParams, page, limit }
        : { type, page, limit };
      const res = await getInstructions(params, token);
      if (res && res.ok) {
        setList(res.data || []);
        setTotal(res.total || 0);
      } else {
        setList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('요청 목록 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  }, [type, token, isSearching, searchParams, page, limit]);

  useEffect(() => {
    fetchInstructions();
  }, [fetchInstructions]);

  const handleRowClick = (id) => {
    navigate(`/pms/instructions/view/${id}`);
  };

  // 검색
  const handleSearch = () => {
    if (searchType !== 'status' && (!searchText || searchText.trim() === '')) {
      alert("검색어가 지정되지 않았습니다.");
      return;
    }
    const params = {};
    if (searchType === 'project_name') params.project_name = searchText;
    if (searchType === 'subject') params.subject = searchText;
    if (searchType === 'author') params.author = searchText;
    if (searchType === 'leader') params.leader = searchText;



    setSearchParams(params);
    setIsSearching(true);
    setPage(1);
  };

  const handleReset = () => {
    setIsSearching(false);
    setSearchType('project_name');
    setSearchText('');
    setSearchParams({});
    setPage(1);
  };

  return (
    <>
      <div className="container-fluid p-4">
        {/* 🚀 상단 헤더 & 그라데이션 타이틀 */}
        {/* 🚀 상단 헤더 & 타이틀 */}
        <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded-3 shadow-sm border border-light">
          <h4 className="mb-0 fw-bold text-dark d-flex align-items-center">
            <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
              <i className={`fas ${type === 'given' ? 'fa-paper-plane' : 'fa-inbox'} fs-5`}></i>
            </div>
            {type === 'given' ? '요청한 프로젝트 목록' : '요청받은 프로젝트 목록'}
            
            {isSearching ? (
              <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 ms-3 rounded-pill fw-semibold shadow-sm px-2 py-1" style={{ fontSize: '0.55em', letterSpacing: '-0.5px', verticalAlign: 'middle' }}>
                <i className="fas fa-search me-1"></i> 검색 결과: 총 {total}건
              </span>
            ) : (
              <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 ms-3 rounded-pill fw-semibold shadow-sm px-2 py-1" style={{ fontSize: '0.55em', letterSpacing: '-0.5px', verticalAlign: 'middle' }}>
                <i className="fas fa-list me-1"></i> 총 {total}건
              </span>
            )}
          </h4>
          <div className="d-flex gap-2">
            <style>{`
              .refresh-btn:hover .fa-sync-alt { transform: rotate(180deg); }
              .refresh-btn .fa-sync-alt { transition: transform 0.3s ease-in-out; }
              .header-btn { transition: all 0.2s ease-in-out; }
              .header-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 6px rgba(0,0,0,0.08) !important; }
            `}</style>
            <button className="btn btn-primary fw-bold btn-sm header-btn shadow-sm" onClick={() => navigate('/pms/instructions/new')}>
              <i className="fas fa-plus me-1"></i>프로젝트 요청 등록
            </button>
            <button className="btn btn-light btn-sm d-flex align-items-center fw-bold text-dark refresh-btn border header-btn shadow-sm" onClick={fetchInstructions}>
              <i className="fas fa-sync-alt me-1 text-primary"></i> 새로고침
            </button>
          </div>
        </div>

        {/* 🔍 검색 영역 */}
        <div className="card mb-0 border-0 shadow-sm rounded-4 rounded-bottom-0">
          <div className="card-body row gx-2 align-items-center m-0" style={{ paddingTop: "15px", paddingBottom: "0px" }}>
            <div className="col-auto">
              <select className="form-select" value={searchType} onChange={(e) => {
                setSearchType(e.target.value);
                setSearchText('');
              }}>
                <option value="project_name">프로젝트명</option>
                <option value="subject">요청 제목</option>
                <option value="author">요청자</option>
                <option value="leader">책임자(PM)</option>
              </select>
            </div>

            <div className="col-md-3" style={{ minWidth: '250px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="검색어 입력"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
              />
            </div>          <div className="col-auto">
              <button className="btn btn-secondary px-3" onClick={handleSearch} style={{ backgroundColor: '#6c8496', border: 'none' }}>
                검색
              </button>
            </div>
            <div className="col-auto">
              <button className="btn btn-outline-secondary px-3" onClick={handleReset}>
                초기화
              </button>
            </div>
          </div>
        </div>

        {/* 📋 데이터 리스트 영역 */}
        <div className="card border-0 shadow-sm rounded-4 rounded-top-0 overflow-hidden">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0" style={{ minWidth: '1100px', tableLayout: 'fixed' }}>
              <thead className="table-light">
                <tr style={{ height: '40px' }}>
                  <th className="text-center text-dark fw-bold align-middle px-0" style={{ width: '90px', borderBottom: '2px solid #ced4da' }}>
                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '4px', height: '100%' }}>
                      <span
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setGuidePos({ top: rect.bottom + 6, left: rect.left });
                        }}
                        onMouseLeave={() => setGuidePos(null)}
                        style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
                      >
                        <i className="fas fa-question-circle text-muted" style={{ fontSize: '1.0rem', marginTop: '-1px' }}></i>
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}>상태</span>
                    </div>
                  </th>
                  <th className="text-dark fw-bold align-middle" style={{ width: '20%', borderBottom: '2px solid #ced4da' }}>프로젝트명</th>
                  <th className="text-dark fw-bold align-middle" style={{ width: '35%', borderBottom: '2px solid #ced4da' }}>요청 제목</th>
                  <th className="text-center text-dark fw-bold align-middle" style={{ width: '18%', borderBottom: '2px solid #ced4da' }}>요청자</th>
                  <th className="text-center text-dark fw-bold align-middle" style={{ width: '27%', borderBottom: '2px solid #ced4da' }}>책임자(PM)</th>
                  <th className="text-center text-dark fw-bold align-middle" style={{ width: '100px', borderBottom: '2px solid #ced4da' }}>기한(마감일)</th>
                  <th className="text-center text-dark fw-bold align-middle" style={{ width: '100px', borderBottom: '2px solid #ced4da' }}>작성일</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      <div className="spinner-border text-secondary me-2" role="status" size="sm"></div>
                      데이터를 불러오는 중입니다...
                    </td>
                  </tr>
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      <i className="fas fa-folder-open mb-3 fs-2 text-light"></i>
                      <p className="mb-0 fs-6 text-secondary">목록이 존재하지 않습니다.</p>
                    </td>
                  </tr>
                ) : (
                  list.map(item => {
                    // 상태 뱃지 표시 로직
                    let displayStatus = item.status;
                    const statusInfo = statusBadgeMap[displayStatus] || { label: displayStatus, className: 'bg-secondary' };

                    return (
                      <tr key={item.id} onClick={() => handleRowClick(item.id)} style={{ cursor: 'pointer', transition: 'all 0.2s ease-in-out' }}>
                        <td className="text-center">
                          <span className={`badge rounded-pill px-3 py-2 ${statusInfo.className} shadow-sm`} style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="fw-bold text-dark fs-6 px-3">
                          <div className="text-truncate text-secondary" style={{ fontSize: '0.85rem' }} title={item.title || '-'}>
                            {item.title || '-'}
                          </div>
                        </td>
                        <td className="fw-bolder text-dark fs-6 px-3">
                          <div className="text-truncate" title={item.subject || '-'}>
                            {item.subject || '-'}
                          </div>
                        </td>
                        <td className="text-center">
                          {(() => {
                            const display = item.author?.name
                              ? `${item.author.name}${item.author.org?.position ? ' / ' + item.author.org.position : ''}${item.author.org?.dept_name ? '(' + item.author.org.dept_name + ')' : ''}`
                              : '알수없음';
                            return (
                              <div className="badge bg-light text-dark border px-2 py-1 text-truncate" style={{ fontSize: '0.75rem', maxWidth: '100%', display: 'inline-block' }} title={display}>
                                <i className="fas fa-user me-1 text-secondary"></i>{display}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center w-100 position-relative"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const isBottom = rect.top > window.innerHeight / 2;
                              setHoveredPmInfo({ id: item.id, rect, isBottom });
                            }}
                            onMouseLeave={() => setHoveredPmInfo(null)}>
                            {(() => {
                              const leaders = item.leaders || [];
                              if (leaders.length === 0) return <span className="text-muted">지정안됨</span>;

                              let myLeader = type === 'received' ? leaders.find(l => l.user?.userid === user?.userid) : null;
                              if (!myLeader) myLeader = leaders[0];

                              const myName = myLeader?.user?.name || '-';
                              const myPos = myLeader?.user?.org?.position || '';
                              const myDept = myLeader?.user?.org?.dept_name || '';
                              const display = myPos
                                ? `${myName} ${myPos}${myDept ? '(' + myDept + ')' : ''}`
                                : myName;

                              let statusNodes = null;

                              const groupedNames = { completed: [], in_progress: [], ready: [], review: [], rejected: [], pending: [], canceled: [] };

                              if (leaders.length > 1) {
                                const projects = item.projects || [];
                                leaders.forEach(l => {
                                  const p = projects.find(proj => proj.author_id === l.leader_id);
                                  const lName = l.user?.name || '-';
                                  const lPos = l.user?.org?.position || '';
                                  const lDisplay = lPos ? `${lName} ${lPos}` : lName;

                                  if (!p) {
                                    groupedNames.pending.push(lDisplay);
                                  } else if (p.status === 'completed') {
                                    groupedNames.completed.push(lDisplay);
                                  } else if (p.status === 'in_progress' || p.status === 'ready') {
                                    groupedNames.in_progress.push(lDisplay);
                                  } else if (p.status === 'review') {
                                    groupedNames.review.push(lDisplay);
                                  } else if (p.status === 'rejected') {
                                    groupedNames.rejected.push(lDisplay);
                                  } else if (p.status === 'canceled') {
                                    groupedNames.canceled.push(lDisplay);
                                  } else {
                                    groupedNames.pending.push(lDisplay);
                                  }
                                });

                                const typeCounts = [];
                                // 1순위: 반려
                                if (groupedNames.rejected.length > 0) typeCounts.push({ key: 'rej', label: '반려', count: groupedNames.rejected.length, color: '#dc3545' });
                                // 2순위: 검토
                                if (groupedNames.review.length > 0) typeCounts.push({ key: 'rev', label: '검토', count: groupedNames.review.length, color: '#0dcaf0' });
                                // 3순위: 대기
                                if (groupedNames.pending.length > 0) typeCounts.push({ key: 'pen', label: '대기', count: groupedNames.pending.length, color: '#ffc107' });
                                // 4순위: 진행
                                if (groupedNames.in_progress.length > 0) typeCounts.push({ key: 'inp', label: '진행', count: groupedNames.in_progress.length, color: '#0d6efd' });
                                // 5순위: 완료
                                if (groupedNames.completed.length > 0) typeCounts.push({ key: 'cmp', label: '완료', count: groupedNames.completed.length, color: '#198754' });
                                // 기타: 취소
                                if (groupedNames.canceled.length > 0) typeCounts.push({ key: 'cnc', label: '취소', count: groupedNames.canceled.length, color: '#6c757d' });

                                if (typeCounts.length > 0) {
                                  const visibleTypes = typeCounts.slice(0, 2);
                                  const finalNodes = visibleTypes.map((t, idx) => (
                                    <span key={t.key}>
                                      {idx > 0 && ' '}
                                      <span style={{ color: t.color }} className="fw-bold">{t.label}{t.count}</span>
                                    </span>
                                  ));

                                  if (typeCounts.length > 2) {
                                    finalNodes.push(
                                      <span key="etc" className="text-secondary">
                                        {' '}외 {typeCounts.length - 2}건
                                      </span>
                                    );
                                  }

                                  statusNodes = (
                                    <span>
                                      {' ['}
                                      {finalNodes}
                                      {']'}
                                    </span>
                                  );
                                }
                              }

                              const renderNames = (names) => {
                                const chunks = [];
                                for (let i = 0; i < names.length; i += 4) {
                                  chunks.push(names.slice(i, i + 4).join(', '));
                                }
                                return (
                                  <div className="lh-sm" style={{ fontSize: '11px', color: '#e9ecef', whiteSpace: 'pre-wrap' }}>
                                    {chunks.join(',\n')}
                                  </div>
                                );
                              };

                              return (
                                <>
                                  <div className="badge bg-light text-dark border px-2 py-1 text-truncate shadow-sm" style={{ fontSize: '0.75rem', maxWidth: '100%', display: 'inline-block', cursor: leaders.length > 1 ? 'pointer' : 'default' }}>
                                    <i className={`fas ${leaders.length === 1 ? 'fa-user' : 'fa-users'} me-1 text-secondary`}></i>
                                    {leaders.length === 1 ? display : `${display} 외 ${leaders.length - 1}명`}
                                    {statusNodes}
                                  </div>

                                  {leaders.length > 1 && hoveredPmInfo?.id === item.id && (
                                    <div className="position-fixed shadow-lg rounded-3 p-2 text-start"
                                      style={{
                                        left: hoveredPmInfo.rect.left + hoveredPmInfo.rect.width / 2,
                                        top: hoveredPmInfo.isBottom ? 'auto' : hoveredPmInfo.rect.bottom + 8,
                                        bottom: hoveredPmInfo.isBottom ? window.innerHeight - hoveredPmInfo.rect.top + 8 : 'auto',
                                        transform: 'translateX(-50%)',
                                        zIndex: 9999, backgroundColor: '#343a40', color: '#f8f9fa',
                                        pointerEvents: 'none', minWidth: '220px'
                                      }}>
                                      <div style={{
                                        position: 'absolute',
                                        top: hoveredPmInfo.isBottom ? 'auto' : '-4px',
                                        bottom: hoveredPmInfo.isBottom ? '-4px' : 'auto',
                                        left: '50%', transform: 'translateX(-50%) rotate(45deg)',
                                        width: '8px', height: '8px', backgroundColor: '#343a40'
                                      }}></div>
                                      <div className="d-flex flex-column gap-2 pe-1">
                                        {groupedNames.rejected.length > 0 && (
                                          <div>
                                            <div className="text-danger fw-bold mb-1" style={{ fontSize: '11px' }}>🔴 반려 ({groupedNames.rejected.length}명)</div>
                                            {renderNames(groupedNames.rejected)}
                                          </div>
                                        )}
                                        {groupedNames.review.length > 0 && (
                                          <div>
                                            <div className="text-info fw-bold mb-1" style={{ fontSize: '11px' }}>🟣 검토 ({groupedNames.review.length}명)</div>
                                            {renderNames(groupedNames.review)}
                                          </div>
                                        )}
                                        {groupedNames.pending.length > 0 && (
                                          <div>
                                            <div className="text-warning fw-bold mb-1" style={{ fontSize: '11px' }}>🟡 대기 ({groupedNames.pending.length}명)</div>
                                            {renderNames(groupedNames.pending)}
                                          </div>
                                        )}
                                        {groupedNames.in_progress.length > 0 && (
                                          <div>
                                            <div className="text-primary fw-bold mb-1" style={{ fontSize: '11px' }}>🔵 진행 ({groupedNames.in_progress.length}명)</div>
                                            {renderNames(groupedNames.in_progress)}
                                          </div>
                                        )}
                                        {groupedNames.completed.length > 0 && (
                                          <div>
                                            <div className="text-success fw-bold mb-1" style={{ fontSize: '11px' }}>🟢 완료 ({groupedNames.completed.length}명)</div>
                                            {renderNames(groupedNames.completed)}
                                          </div>
                                        )}
                                        {groupedNames.canceled.length > 0 && (
                                          <div>
                                            <div className="text-secondary fw-bold mb-1" style={{ fontSize: '11px' }}>⚫ 취소 ({groupedNames.canceled.length}명)</div>
                                            {renderNames(groupedNames.canceled)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="text-center text-danger fw-bold">
                          {item.due_date ? item.due_date.substring(0, 10) : '-'}
                        </td>
                        <td className="text-center text-muted">
                          {item.created_at ? item.created_at.substring(0, 10) : '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 📄 페이지네이션 */}
        {(() => {
          const totalPages = Math.max(1, Math.ceil(total / limit));
          const maxVisible = 5;
          let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
          let endPage = Math.min(totalPages, startPage + maxVisible - 1);
          if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
          const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

          return (
            <div className="position-relative d-flex justify-content-center align-items-center mt-3 p-2">
              <div className="position-absolute" style={{ left: '10px' }}>
                <span className="badge rounded text-white" style={{ backgroundColor: '#777b8c', fontSize: '13px', padding: '0.25rem 0.6rem' }}>
                  {page} / {totalPages} (총 : {total})
                </span>
              </div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link text-secondary" onClick={() => setPage(p => Math.max(1, p - 1))}>« 이전</button>
                  </li>
                  {total > 0 && pages.map(p => (
                    <li key={p} className={`page-item ${page === p ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                    </li>
                  ))}
                  <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link text-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))}>다음 »</button>
                  </li>
                </ul>
              </nav>
            </div>
          );
        })()}

        {/* 상태 우선순위 안내 레이어 (fixed - table overflow 밖에 렌더링) */}
        {guidePos && (
          <div
            style={{
              position: 'fixed',
              top: guidePos.top,
              left: guidePos.left,
              zIndex: 99999,
              backgroundColor: '#fff',
              border: '1px solid #dee2e6',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              padding: '14px 16px',
              minWidth: '340px',
              textAlign: 'left',
              fontSize: '0.75rem', /* 0.8rem -> 0.75rem으로 축소 */
              lineHeight: '1.7',
              pointerEvents: 'none',
              animation: 'fadeInGuide 0.15s ease'
            }}
          >
            <style>{`@keyframes fadeInGuide { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }`}</style>
            <div style={{ fontWeight: '700', fontSize: '0.85rem', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0', color: '#333' }}>
              <i className="fas fa-info-circle me-1 text-info"></i> 상태 우선순위 안내
            </div>
            <div style={{ color: '#888', fontSize: '0.7rem', marginBottom: '10px' }}>
              여러 담당자가 있을 경우, 높은 순위의 상태가 우선 적용됩니다.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#fff5f5', borderRadius: '8px', border: '1px solid #ffd6d6' }}>
                <span style={{ fontSize: '0.9rem' }}>🥇</span>
                <span><strong style={{ color: '#dc3545' }}>1순위 [반려됨]</strong><span style={{ color: '#888' }}> — 단 1명이라도 반려한 경우</span></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #c5e3ff' }}>
                <span style={{ fontSize: '0.9rem' }}>🥈</span>
                <span><strong style={{ color: '#0d6efd' }}>2순위 [검토중]</strong><span style={{ color: '#888' }}> — 반려 없고, 1명이라도 검토 중인 경우</span></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#fffdf0', borderRadius: '8px', border: '1px solid #ffe89a' }}>
                <span style={{ fontSize: '0.9rem' }}>🥉</span>
                <span><strong style={{ color: '#856404' }}>3순위 [대기중]</strong><span style={{ color: '#888' }}> — 프로젝트 생성을 대기 중인 인원이 있는 경우</span></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#f0fff5', borderRadius: '8px', border: '1px solid #b2f0c8' }}>
                <span style={{ fontSize: '0.9rem' }}>🏃</span>
                <span><strong style={{ color: '#198754' }}>4순위 [진행중]</strong><span style={{ color: '#888' }}> — 진행중(시작전 포함)인 인원이 있는 경우</span></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <span style={{ fontSize: '0.9rem' }}>🏁</span>
                <span><strong style={{ color: '#6c757d' }}>5순위 [완료됨]</strong><span style={{ color: '#888' }}> — 배정된 전원이 100% 완료한 경우에만</span></span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default InstructionList;
